/* eslint-disable jira/integration/test-id-by-folder-structure */
import React, { memo, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { IconButton } from '@atlaskit/button/new';
import { DropdownItemGroup } from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import Popup, { type TriggerProps } from '@atlaskit/popup'; // ignore-for-ENGHEALTH-17759
import { Box, xcss } from '@atlaskit/primitives';
import { Tokens } from '@atlassian/jira-custom-theme-constants/src/constants.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	DROPDOWN,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { TabMenu_container$key } from '@atlassian/jira-relay/src/__generated__/TabMenu_container.graphql';
import type { TabMenu_tab$key } from '@atlassian/jira-relay/src/__generated__/TabMenu_tab.graphql';
import { ThemedIconButton } from '@atlassian/jira-project-theme-components/src/ui/themed-button-new/ThemedButtonNew.tsx';
import { HideItem } from './HideItem.tsx';
import messages from './messages.tsx';
import { MoveItem } from './MoveItem.tsx';
import { RenameItem } from './RenameItem.tsx';
import { SetAsDefaultItem } from './SetAsDefaultItem.tsx';

export type TabMenuProps = {
	tabFragment: TabMenu_tab$key;
	containerFragment: TabMenu_container$key;
	// id of the tab that appears before the tab being rendered, for sorting purposes. Will be `null` when it's the first tab.
	beforeItemId?: string | null;
	// id of the tab that appears after the tab being rendered, for sorting purposes. Will be `null` when it's the last tab.
	afterItemId?: string | null;
	position?: number;
	// whether the menu is attached to an overflowing tab
	isOverflowing?: boolean;
	isOpen: boolean;
	setIsOpen: (setOpen: boolean | ((isOpen: boolean) => boolean)) => void;
	onRenameClick?: () => void;
};

export const TabMenu = memo(
	({
		tabFragment,
		containerFragment,
		beforeItemId,
		afterItemId,
		position,
		isOverflowing,
		isOpen,
		setIsOpen,
		onRenameClick,
	}: TabMenuProps): React.JSX.Element => {
		const { formatMessage } = useIntl();
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const tabData = useFragment(
			graphql`
				fragment TabMenu_tab on JiraNavigationItem {
					...SetAsDefaultItem_tab
					...MoveItem_tab
					...HideItem_tab
					id
					__id
					typeKey @required(action: THROW)
					isDefault @required(action: THROW)
					canRename
					canRemove
					canSetAsDefault
					... on JiraAppNavigationItem {
						appId
					}
				}
			`,
			tabFragment,
		);
		const canRename = Boolean(tabData?.canRename);
		const canRemove = Boolean(tabData?.canRemove);
		const canSetAsDefault = Boolean(tabData?.canSetAsDefault);

		const containerData = useFragment(
			graphql`
				fragment TabMenu_container on JiraContainerNavigationResult {
					...SetAsDefaultItem_container
					...MoveItem_container
					...HideItem_container
				}
			`,
			containerFragment,
		);

		const toggleMenuOpen = useCallback(() => {
			setIsOpen((isMenuOpen) => !isMenuOpen);
		}, [setIsOpen]);

		const closeMenu = useCallback(() => setIsOpen(false), [setIsOpen]);

		const handleHideClick = useCallback(() => {
			closeMenu();

			fireUIAnalytics(createAnalyticsEvent({}), 'dropdownItem clicked', 'projectNavigationTab', {
				action: 'remove',
				itemType: tabData.typeKey.toLowerCase(),
				itemId: tabData.appId ?? undefined,
				isInMore: Boolean(isOverflowing),
			});
		}, [closeMenu, createAnalyticsEvent, tabData.typeKey, tabData.appId, isOverflowing]);

		const handleSetAsDefault = useCallback(() => {
			closeMenu();
			fireUIAnalytics(createAnalyticsEvent({}), 'dropdownItem clicked', 'projectNavigationTab', {
				action: 'setAsDefault',
				itemType: tabData.typeKey.toLowerCase(),
				itemId: tabData.appId ?? undefined,
				isInMore: Boolean(isOverflowing),
			});
		}, [closeMenu, createAnalyticsEvent, isOverflowing, tabData.appId, tabData.typeKey]);

		const handleRenameNavigationItem = useCallback(() => {
			closeMenu();
			onRenameClick?.();

			fireUIAnalytics(createAnalyticsEvent({}), 'dropdownItem clicked', 'projectNavigationTab', {
				action: 'rename',
				itemType: tabData.typeKey.toLowerCase(),
				isInMore: Boolean(isOverflowing),
			});
		}, [closeMenu, createAnalyticsEvent, isOverflowing, onRenameClick, tabData.typeKey]);

		const trigger = (triggerProps: TriggerProps) => {
			if (isOverflowing) {
				return (
					<IconButton
						{...triggerProps}
						label={formatMessage(messages.viewOptions)}
						icon={MoreIcon}
						isTooltipDisabled={false}
						tooltip={{
							hideTooltipOnClick: true,
							content: formatMessage(messages.moreActionsTooltip),
						}}
						onClick={toggleMenuOpen}
						appearance="subtle"
						spacing="compact"
						isSelected={isOpen}
					/>
				);
			}

			return (
				<ThemedIconButton
					{...triggerProps}
					label={formatMessage(messages.viewOptions)}
					icon={(iconProps) => (
						// @ts-expect-error - TS2322
						<MoreIcon {...iconProps} color={!isOpen ? Tokens.COLOR_LINK : undefined} />
					)}
					isTooltipDisabled={false}
					tooltip={{
						hideTooltipOnClick: true,
						content: formatMessage(messages.moreActionsTooltip),
					}}
					onClick={toggleMenuOpen}
					appearance="subtle"
					spacing="compact"
					isSelected={isOpen}
				/>
			);
		};

		const hasTopGroup = canSetAsDefault || canRename;
		const hasMiddleGroup = position !== undefined;

		return (
			<Popup
				trigger={trigger}
				isOpen={isOpen}
				onClose={closeMenu}
				placement={isOverflowing ? 'right-start' : 'bottom-start'}
				shouldUseCaptureOnOutsideClick
				content={() => (
					<ContextualAnalyticsData sourceType={DROPDOWN} sourceName="navigationKitTabMenu">
						<Box
							xcss={containerStyles}
							testId="navigation-kit-tab-menu.ui.tab-menu-options"
							role="menu"
						>
							{hasTopGroup && (
								<DropdownItemGroup>
									{canSetAsDefault && (
										<SetAsDefaultItem
											onClick={handleSetAsDefault}
											tabFragment={tabData}
											containerFragment={containerData}
										/>
									)}
									{canRename && <RenameItem onClick={handleRenameNavigationItem} />}
								</DropdownItemGroup>
							)}

							{hasMiddleGroup && (
								<MoveItem
									hasSeparator={hasTopGroup}
									id={tabData?.id}
									dataId={tabData?.__id}
									beforeItemId={beforeItemId}
									afterItemId={afterItemId}
									position={position}
									onClick={closeMenu}
									tabFragment={tabData}
									containerFragment={containerData}
									isOverflowing={isOverflowing}
								/>
							)}
							{canRemove && !tabData.isDefault && (
								<DropdownItemGroup hasSeparator={hasTopGroup || hasMiddleGroup}>
									<HideItem
										onClick={handleHideClick}
										tabFragment={tabData}
										containerFragment={containerData}
									/>
								</DropdownItemGroup>
							)}
						</Box>
					</ContextualAnalyticsData>
				)}
			/>
		);
	},
);

const containerStyles = xcss({
	minWidth: '124px',
});
